import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { Accordion, Alert, Box, Tag, Text, TextButton, VisuallyHidden } from '@zillow/constellation-10';
import { IconErrorFilled, IconLightBulbFilled } from '@zillow/constellation-icons';

import {
  EnterpriseListingEvent,
  EnterprisePageFeature,
  RentalManagerEnterpriseEventTrackingWebApiHandler_trackCustomerListingEvent,
  RentalManagerEnterpriseEventTrackingWebApiHandler_trackPageVisit,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

import { useGetListingDetails, useGetListingRecommendations } from '../../hooks/queries/properties';
import { useScreenSize } from '../../hooks/useScreenSize';
import propertyUtils from '../../utils/propertyUtils';
import stringHelper from '../../utils/stringHelper';
import { SCREENING_CRITERIA_INDEX, SCROLL_ANIMATION_DELAY } from './utils';

import { REVIEW_PAGE_SECTIONS } from '../../constants/ListingDetailConstants';
import StringConstants from '../../constants/StringConstants';
import { getTourCalendarRoute } from '../../constants/urls';
import { SF_MODAL_COMPONENTS } from '../CreateListingPage/steps/Review/constants';

import useValidationErrorGuide from '../CreateListingPage/steps/Review/components/ValidationErrorGuide/useValidationErrorGuide';
import { FieldEditModal } from '../modals/FieldEditModal';

import { emitApplicationsClickEvent } from '../../analytics/events/reviewPage';
import AccordionContentContainer from '../AccordionContentContainer';
import { useAuth } from '../AuthProvider';
import AmenitiesContent from '../CreateListingPage/steps/Review/sections/AmenitiesContent';
import CostsAndFeesContent from '../CreateListingPage/steps/Review/sections/CostsAndFeesContent';
import FinalDetailsContent from '../CreateListingPage/steps/Review/sections/FinalDetailsContent';
import PropertyInformationContent from '../CreateListingPage/steps/Review/sections/PropertyInformationContent';
import RentDetailsContent from '../CreateListingPage/steps/Review/sections/RentDetailsContent';
import ScreeningCriteriaContent from '../CreateListingPage/steps/Review/sections/ScreeningCriteriaContent';
import EmailVerificationModal from '../PostingPath/EmailFormField';
import MediaContent from '../PostingPath/MediaContent';

import type { MouseEvent } from 'react';
import type { ErrorMapping } from '../CreateListingPage/steps/Review/components/ValidationErrorGuide/errorMapping';
import type { ErrorGuideState } from '../CreateListingPage/steps/Review/components/ValidationErrorGuide/useValidationErrorGuide';

type Props = {
  requiredFieldsErrors: Record<string, boolean>;
  onErrorGuideChange?: (state: ErrorGuideState) => void;
};

const SFAccordionContainer = ({ requiredFieldsErrors, onErrorGuideChange }: Props): JSX.Element => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const { user } = useAuth();
  const { isScreenMobile } = useScreenSize();

  const { data: listingDetailsData } = useGetListingDetails(listingAlias);
  const { recommendationsBySection } = useGetListingRecommendations({
    listingAlias,
    isMultifamily: false,
  });

  const active = listingDetailsData?.active || false;

  // Feature flags for conditional rendering
  const canAccessScreeningCriteriaInListingFlow = listingDetailsData?.listingCriteria?.isEnabled || false;
  const canAccessSFCostsAndFees = true; // SF properties always have access to costs and fees

  const [componentKey, setComponentKey] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const openTourModalRef = useRef<(() => void) | null>(null);
  const [expandedAccordionIndices, setExpandedAccordionIndices] = useState<Array<string>>([]);

  const [displayEmailVerification, setDisplayEmailVerification] = useState(false);

  // feeAmount is already computed in container, only needed locally for error guide
  const feeAmount = null;

  const isRoomForRent = propertyUtils.isRoomForRent({ listing: listingDetailsData });

  const isDisabled = Boolean(
    !user?.features?.canUpdateListing ||
    listingDetailsData?.isArchived ||
    listingDetailsData?.restrictions?.isUpdateListingRestricted,
  );

  const handleNavigateToError = (mapping: ErrorMapping) => {
    const { accordionIndex, componentKey: errorComponentKey } = mapping;

    setExpandedAccordionIndices([String(accordionIndex)]);

    if (errorComponentKey === 'TouringSection') {
      setTimeout(() => openTourModalRef.current?.(), SCROLL_ANIMATION_DELAY);
      return;
    }

    if (errorComponentKey === SF_MODAL_COMPONENTS.EmailFormField.componentKey) {
      const emailFormFieldElement = document.querySelector(
        `[value="${SF_MODAL_COMPONENTS.EmailFormField.componentKey}"]`,
      ) as HTMLButtonElement;

      if (emailFormFieldElement) {
        emailFormFieldElement.click();
        emailFormFieldElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      return;
    }

    if (errorComponentKey === SF_MODAL_COMPONENTS.PhoneNumberFormField.componentKey) {
      const phoneNumberFormFieldElement = document.querySelector(
        `[value="${SF_MODAL_COMPONENTS.PhoneNumberFormField.componentKey}"]`,
      ) as HTMLButtonElement;

      if (phoneNumberFormFieldElement) {
        phoneNumberFormFieldElement.click();
        phoneNumberFormFieldElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      return;
    }

    if (errorComponentKey) {
      setComponentKey(errorComponentKey);
      setIsOpen(true);
    } else {
      setTimeout(() => {
        const accordionElement = document.querySelector(`[data-accordion-index="${accordionIndex}"]`);
        if (accordionElement) {
          accordionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, SCROLL_ANIMATION_DELAY);
    }
  };

  useValidationErrorGuide({
    requiredFieldsErrors,
    currentProperty: listingDetailsData,
    feeAmount,
    isRoomForRent,
    onErrorGuideChange,
    onNavigateToError: handleNavigateToError,
  });

  useEffect(() => {
    if (user?.customers?.length && !user?.impersonatorEmail && !user?.impersonatorUserToken) {
      RentalManagerEnterpriseEventTrackingWebApiHandler_trackPageVisit({
        page: EnterprisePageFeature.PROPERTY_EDIT,
      }).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (!listingDetailsData) {
      return;
    }
    if (user?.customers?.length && !user?.impersonatorEmail && !user?.impersonatorUserToken) {
      RentalManagerEnterpriseEventTrackingWebApiHandler_trackCustomerListingEvent({
        feedId: listingDetailsData?.feedId || '',
        feedListingId: listingDetailsData?.feedListingId || '',
        event: EnterpriseListingEvent.VIEW_PROPERTY,
      }).catch(() => {});
    }
  }, [listingDetailsData, user]);

  useEffect(() => {
    if (!isOpen) {
      setComponentKey('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (router.query.scrollTo === 'screeningCriteria') {
      setExpandedAccordionIndices([SCREENING_CRITERIA_INDEX]);

      // Wait for accordion to expand before scrolling
      setTimeout(() => {
        const accordionElement = document.querySelector(`[data-accordion-index="${SCREENING_CRITERIA_INDEX}"]`);
        if (accordionElement) {
          accordionElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            container: 'nearest',
          } as ScrollIntoViewOptions);
        }
      }, 300);
    }
  }, [router.query.scrollTo]);

  const { component: ModalBodyComponent, title: modalTitle = '' } = useMemo(
    () => SF_MODAL_COMPONENTS[componentKey as keyof typeof SF_MODAL_COMPONENTS] || { component: null, title: '' },
    [componentKey],
  );

  const trackEditClick = (groupTitle?: string, fieldTitle?: string) => {
    if (!groupTitle || !fieldTitle) {
      return;
    }

    if (fieldTitle.toLowerCase().includes('applications')) {
      emitApplicationsClickEvent({
        currentProperty: listingDetailsData,
        user,
      });
    }
  };

  const handleEditClick = (event: MouseEvent<HTMLButtonElement>) => {
    const { parentElement, value: aComponentKey } = event.currentTarget;

    let fieldTitle = parentElement?.firstChild?.textContent
      ? stringHelper.startCaseRemoveSpace(parentElement.firstChild.textContent)
      : undefined;
    const groupTitle = parentElement?.parentElement?.parentElement?.getAttribute('data-group-title') || undefined;

    if (fieldTitle && fieldTitle.includes(StringConstants.ERROR)) {
      fieldTitle = `Incomplete${fieldTitle.substring(
        fieldTitle.indexOf(StringConstants.ERROR) + StringConstants.ERROR.length,
      )}`;
    }

    trackEditClick(groupTitle, fieldTitle);

    // IncompletePhone comes from the title passed to AccordionIconError and text from Phone
    if (
      fieldTitle === SF_MODAL_COMPONENTS.PhoneNumberFormField.title ||
      fieldTitle === `Incomplete${SF_MODAL_COMPONENTS.PhoneNumberFormField.title}`
    ) {
      return;
    }

    if (
      fieldTitle === SF_MODAL_COMPONENTS.EmailFormField.title ||
      fieldTitle === `Incomplete${SF_MODAL_COMPONENTS.EmailFormField.title}`
    ) {
      setDisplayEmailVerification(true);

      return;
    }

    setComponentKey(aComponentKey);
    setIsOpen(true);
  };

  const getAccordionTag = ({
    reviewSection,
    isIncomplete = false,
  }: {
    reviewSection: string;
    isIncomplete: boolean;
  }) => {
    if (
      recommendationsBySection[reviewSection] &&
      Object.keys(recommendationsBySection[reviewSection]).length &&
      !isIncomplete
    ) {
      return (
        <Tag tone="blue" icon={<IconLightBulbFilled />} size="sm">
          {isScreenMobile
            ? Object.keys(recommendationsBySection[reviewSection]).length
            : `${stringHelper.pluralize(
                Object.keys(recommendationsBySection[reviewSection]).length,
                'recommendation',
                'recommendations',
              )}`}
        </Tag>
      );
    }

    return (
      isIncomplete &&
      !isDisabled && (
        <Tag tone="yellow" icon={<IconErrorFilled />} size="sm">
          Incomplete
        </Tag>
      )
    );
  };

  return (
    <div data-testid="SFAccordionContainer">
      <Box css={{ marginBottom: '400' }}>
        {Boolean(!!listingDetailsData?.lastActivatedMs && !listingDetailsData?.isFeed) && (
          <Alert
            css={{ marginTop: '400' }}
            actionButton={
              active && (
                <TextButton textStyle="body-bold" asChild>
                  <Link href={getTourCalendarRoute({ listingAlias })} target="_blank" rel="noopener noreferrer">
                    View or update your availability
                  </Link>
                </TextButton>
              )
            }
          >
            {!active ? (
              <Text>After you publish your listing, you&apos;ll be able to set your availability for tours</Text>
            ) : (
              <Text>
                Zillow&apos;s new tour scheduling feature lets you add your availability to a calendar for easier
                booking
              </Text>
            )}
          </Alert>
        )}
      </Box>

      <Accordion.Root
        expanded={expandedAccordionIndices}
        onExpandedChange={(values) => {
          setExpandedAccordionIndices(typeof values === 'string' ? [values] : values);
        }}
      >
        <AccordionContentContainer
          tag={getAccordionTag({
            reviewSection: REVIEW_PAGE_SECTIONS.PROPERTY_INFORMATION,
            isIncomplete: requiredFieldsErrors.floorplans,
          })}
          header="Property information"
          content={<PropertyInformationContent onEditClick={handleEditClick} />}
          accordionIndex={0}
        />
        <AccordionContentContainer
          tag={getAccordionTag({
            reviewSection: REVIEW_PAGE_SECTIONS.RENT_DETAILS,
            isIncomplete: requiredFieldsErrors.rentDetails || requiredFieldsErrors.monthlyRent,
          })}
          header="Rent details"
          content={<RentDetailsContent onEditClick={handleEditClick} />}
          accordionIndex={1}
        />
        <AccordionContentContainer
          tag={getAccordionTag({
            reviewSection: REVIEW_PAGE_SECTIONS.MEDIA,
            isIncomplete: requiredFieldsErrors.media,
          })}
          header="Media"
          content={<MediaContent />}
          accordionIndex={2}
        />
        <AccordionContentContainer
          tag={getAccordionTag({
            reviewSection: REVIEW_PAGE_SECTIONS.AMENITIES,
            isIncomplete: requiredFieldsErrors.amenities,
          })}
          header="Amenities"
          content={<AmenitiesContent onEditClick={handleEditClick} />}
          accordionIndex={3}
        />

        {canAccessScreeningCriteriaInListingFlow && (
          <AccordionContentContainer
            tag={getAccordionTag({
              reviewSection: REVIEW_PAGE_SECTIONS.SCREENING_CRITERIA,
              isIncomplete: requiredFieldsErrors.screeningCriteria,
            })}
            header="Screening criteria"
            content={<ScreeningCriteriaContent onEditClick={handleEditClick} />}
            accordionIndex={Number(SCREENING_CRITERIA_INDEX)}
          />
        )}

        {canAccessSFCostsAndFees && (
          <AccordionContentContainer
            tag={getAccordionTag({
              reviewSection: REVIEW_PAGE_SECTIONS.COSTS_AND_FEES,
              isIncomplete: requiredFieldsErrors.costsAndFees,
            })}
            header="Costs and fees"
            content={<CostsAndFeesContent />}
            accordionIndex={5}
          />
        )}

        <AccordionContentContainer
          tag={getAccordionTag({
            reviewSection: REVIEW_PAGE_SECTIONS.CONTACT,
            isIncomplete: requiredFieldsErrors.contact,
          })}
          header="Final details"
          content={<FinalDetailsContent onEditClick={handleEditClick} openTourModalRef={openTourModalRef} />}
          accordionIndex={6}
        />
      </Accordion.Root>

      <FieldEditModal title={modalTitle} isOpen={isOpen} onClose={() => setIsOpen(false)}>
        {ModalBodyComponent && <ModalBodyComponent />}
      </FieldEditModal>

      <VisuallyHidden>
        {displayEmailVerification && <EmailVerificationModal onClose={() => setDisplayEmailVerification(false)} />}
      </VisuallyHidden>
    </div>
  );
};

export default SFAccordionContainer;
